import React, { useEffect } from 'react';

import { track, useMutation } from '@betterment/js-runtime';
import { Banner, Box as SambaBox, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { camelCase as _camelCase } from 'es-toolkit/compat';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';

import type { NoticeItem } from '../data';

const TRACKING_FLOW = 'Summary';

type Props = {
  notice: NoticeItem;
  fetchNextNotice: () => Promise<unknown>;
};

const Notice: React.FC<Props> = ({ notice, fetchNextNotice }) => {
  const { name, analyticsName, cta, body, dismissable, style } = notice;

  const [putDismissNotice] = useMutation<void, { name: string }>(urlBuilder.apiV2MessageDismissals({ name }), {
    method: 'PUT',
  });

  const maybeTrackableLink = cta ? (
    <SambaAutoLink
      color="bmtGrey90"
      style={{ textDecoration: 'underline' }}
      onClick={() => {
        trackClick({
          name: `${analyticsName}_${_camelCase(cta.text)}`,
          module: 'GlobalSystemNotice',
          flow: TRACKING_FLOW,
        });
      }}
      to={cta.path}
      aria-label={cta.text}
    >
      {cta.text}
    </SambaAutoLink>
  ) : null;

  const handleDismissalClick = async () => {
    track('ElementClicked', {
      name: `${analyticsName}_dismiss`,
      module: 'GlobalSystemNotice',
      flow: TRACKING_FLOW,
    });
    const response = await putDismissNotice();
    if (response && !response.errors) await fetchNextNotice();
  };

  useEffect(() => {
    track('ElementViewed', { name: analyticsName, module: 'GlobalSystemNotice', flow: TRACKING_FLOW });
  }, [analyticsName]);

  return (
    <SambaBox
      css={{
        bg: 'background.surface',
        paddingTop: ['space.100', 'space.300'],
        paddingBottom: ['space.300', 'space.300', 'space.0'],
        px: ['space.200', 'space.200', 'space.500'],
      }}
    >
      <Banner variant={style} css={{ maxWidth: '1200px', margin: 'auto' }}>
        <Banner.Content
          css={{ display: 'flex', gap: 'space.100', flexDirection: 'column', md: { flexDirection: 'row' } }}
        >
          <Text>
            {body} {maybeTrackableLink}
          </Text>
        </Banner.Content>
        {dismissable && <Banner.Close label="Dismiss notice" onClick={handleDismissalClick} />}
      </Banner>
    </SambaBox>
  );
};

export default Notice;
